/**
 * RR3 `<Link>` / `<IndexLink>` shim, rendered over TanStack's context router.
 *
 * Like RR3's `<Link>`, this requires a router context (a `RouterProvider`); the
 * app always has one, and component tests get one via the
 * `render-with-router` helper. We render a plain anchor so we can accept RR3's
 * loose `to` (string or `{pathname,query,hash,state}`) plus the separate
 * `query`/`hash`/`state` props, and translate `activeClassName`/`activeStyle`/
 * `onlyActiveOnIndex` to active-state styling — intentionally forgoing
 * TanStack's typed links so the ~130 call sites stay untouched.
 */
import * as React from 'react';

import {routerHistory} from './history';
import {serializePath} from './path';
import {useRouterSnapshot} from './useRouterSnapshot';
import type {LinkProps, LocationDescriptor} from './types';

function resolveTarget(props: LinkProps): LocationDescriptor {
  const {query, hash, state} = props;
  // RR3 allowed `to` to be a function returning a destination.
  const to = typeof props.to === 'function' ? props.to() : props.to;
  // RR3 tolerated a nullish `to` (e.g. from not-yet-loaded data) without
  // throwing — render an anchor to the current path instead.
  if (to == null) {
    return query !== undefined || hash !== undefined || state !== undefined
      ? {pathname: '', query, hash, state}
      : '';
  }
  if (typeof to === 'string') {
    if (query === undefined && hash === undefined && state === undefined) {
      return to;
    }
    return {pathname: to, query, hash, state};
  }
  return {
    ...to,
    query: to.query ?? query,
    hash: to.hash ?? hash,
    state: to.state ?? state
  };
}

function targetPathname(target: LocationDescriptor): string {
  if (typeof target === 'string') {
    return target.split('?')[0].split('#')[0];
  }
  return target.pathname ?? '';
}

function isModifiedEvent(event: React.MouseEvent): boolean {
  return event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
}

function assignRef(ref: React.Ref<HTMLAnchorElement> | undefined, node: HTMLAnchorElement | null) {
  if (typeof ref === 'function') {
    ref(node);
  } else if (ref && typeof ref === 'object') {
    (ref as React.MutableRefObject<HTMLAnchorElement | null>).current = node;
  }
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  function Link(props, forwardedRef) {
    const {
      to,
      query,
      hash,
      state,
      activeClassName,
      activeStyle,
      onlyActiveOnIndex,
      className,
      style,
      onClick,
      target,
      children,
      href: hrefProp,
      replace,
      innerRef,
      ...rest
    } = props;

    const {router, state: routerState} = useRouterSnapshot();
    // Fall back to the global history when rendered outside a RouterProvider
    // (RR3's Link rendered a plain anchor in that case rather than throwing).
    const history = router ? router.history : routerHistory;
    const currentPathname = routerState
      ? routerState.location.pathname
      : routerHistory.location.pathname;

    const resolved = resolveTarget(props);
    const path = serializePath(resolved);
    // When no `to` is given but an `href` is (e.g. `<Anchor href="mailto:…">`,
    // whose default `as` is this Link), respect the passed href.
    const usePassedHref = to == null && hrefProp != null;
    // External / scheme URLs (mailto:, tel:, http(s):, protocol-relative //) are
    // passed straight through to the anchor and not router-navigated, matching RR3.
    const isExternal = usePassedHref || /^([a-z][a-z0-9+.-]*:|\/\/)/i.test(path);
    const href = usePassedHref ? hrefProp : isExternal ? path : history.createHref(path);

    // Derive the active-match pathname from the SERIALIZED (absolute) path, not the
    // raw descriptor: `serializePath` normalizes a relative `to` to a leading-slash
    // path, and `currentPathname` is always absolute, so comparing against the raw
    // relative pathname would never match (active state silently never applies).
    const linkPathname = targetPathname(path);
    const isActive =
      !isExternal &&
      (onlyActiveOnIndex
        ? currentPathname === linkPathname
        : linkPathname !== '' &&
          (currentPathname === linkPathname || currentPathname.startsWith(`${linkPathname}/`)));

    // No real destination: nullish `to` with no href (e.g. `<Anchor>` as a styled
    // button), or a `to` that serializes to empty. RR3 rendered any falsy `to` inert, so
    // treat `''` like nullish — else `history.push('')` navigates to a blank page (#19208).
    const hasNoDestination = !usePassedHref && (to == null || path === '');

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        onClick(event);
      }
      if (event.defaultPrevented || event.button !== 0 || isModifiedEvent(event)) {
        return;
      }
      if (hasNoDestination) {
        // No destination: suppress the native anchor navigation (the empty href
        // would otherwise navigate) but DON'T router-navigate — let the click
        // bubble to a parent handler (e.g. Modal's CloseButtonWrapper span).
        event.preventDefault();
        return;
      }
      if (isExternal || (target && target !== '_self')) {
        // Let the browser handle external/scheme URLs and new-tab targets.
        return;
      }
      event.preventDefault();
      if (replace) {
        history.replace(path, state ?? undefined);
        return;
      }
      history.push(path, state ?? undefined);
    };

    return (
      <a
        {...rest}
        ref={(node) => {
          // CloseButtonWrapper / focus helpers attach a ref to the anchor; RR3's
          // Link accepted refs, so forward both the standard ref and RR3's innerRef.
          assignRef(forwardedRef, node);
          assignRef(innerRef, node);
        }}
        href={href}
        target={target}
        onClick={handleClick}
        className={
          isActive && activeClassName ? `${className ?? ''} ${activeClassName}`.trim() : className
        }
        style={isActive && activeStyle ? {...style, ...activeStyle} : style}
      >
        {children}
      </a>
    );
  }
);

Link.displayName = 'Link';

/** RR3 `<IndexLink>` — a `<Link>` that is only active on an exact path match. */
export const IndexLink = React.forwardRef<HTMLAnchorElement, LinkProps>(
  function IndexLink(props, ref) {
    return <Link {...props} ref={ref} onlyActiveOnIndex />;
  }
);

IndexLink.displayName = 'IndexLink';

export default Link;
